import { type FormEvent, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";

import {
  HotelsAvailabilityActions,
  HotelsAvailabilitySelectors,
  HotelsRawEventName,
  useEmitCoreHotelsRawEventOnce,
} from "@b2bportal/core-hotels";
import {
  HOTELS_AVAILABILITY_PATH,
  HotelNavigationUtility,
} from "@b2bportal/core-utilities";
import type { GuestsSelection, Suggestion } from "@b2bportal/lodging-api";
import { useExperiment } from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useSessionContext } from "@/components/AuthModule/SessionContext";
import { buildPlatformAvailabilityUrl } from "@/utils/commerce/urls";
import { saveRecentHotelSearch } from "@/utils/useRecentSearches";

export const useHotelSearch = () => {
  const { sessionInfo } = useSessionContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useI18nContext();

  const staysPlatformEnabled = useExperiment(
    "StaysCommercePlatformPerkSpotRollout"
  );

  useEmitCoreHotelsRawEventOnce({ name: HotelsRawEventName.HotelSearchLoaded });

  const dateRange = useSelector(
    HotelsAvailabilitySelectors.getSearchFormDateRange
  );
  const guests = useSelector(HotelsAvailabilitySelectors.getSearchFormGuests);
  const place = useSelector(HotelsAvailabilitySelectors.getSearchFormPlace);
  const roomsCount = useSelector(
    HotelsAvailabilitySelectors.getSearchFormRoomsCount
  );

  const handleDateRangeFromChange = (date: string | undefined) => {
    dispatch(HotelsAvailabilityActions.setNewDateFrom(date));
  };
  const handleDateRangeUntilChange = (date: string | undefined) => {
    dispatch(HotelsAvailabilityActions.setNewDateUntil(date));
  };

  const handlePlaceChange = (place: Suggestion) => {
    dispatch(HotelsAvailabilityActions.setNewPlace(place));
  };
  const handleGuestsChange = (guests: GuestsSelection) => {
    dispatch(HotelsAvailabilityActions.setNewGuests(guests));
  };

  const handleRoomsCountChange = (count: number) => {
    dispatch(HotelsAvailabilityActions.setNewRoomsCount(count));
  };

  const isFormValid = useMemo(() => {
    return dateRange?.from != null && dateRange?.until != null && place != null;
  }, [dateRange, place]);

  const availabilityPath = useMemo(() => {
    if (
      isFormValid &&
      place != null &&
      dateRange?.until != null &&
      dateRange?.from != null
    ) {
      saveRecentHotelSearch({
        fromDate: HotelNavigationUtility.formatDateForUrl(dateRange.from),
        untilDate: HotelNavigationUtility.formatDateForUrl(dateRange.until),
        guests,
        roomsCount,
        label: place.label,
        subLabel: place.subLabel,
        place,
        customerId: sessionInfo?.hopperUserId,
      });

      if (staysPlatformEnabled) {
        return buildPlatformAvailabilityUrl({
          place,
          dateRange: { from: dateRange.from, until: dateRange.until },
          guests,
          roomsCount,
          perkspotUserId: sessionInfo?.userDetails.tenantUserId,
          communityId: sessionInfo?.userDetails.communityId,
        });
      }

      const destinationCode = HotelNavigationUtility.placeLabelAndSublabelToUrl(
        place.label,
        place.subLabel
      );
      const searchParams = HotelNavigationUtility.buildSearchParams({
        dateRange: { from: dateRange.from, until: dateRange.until },
        guests,
        roomsCount,
      });

      return `${HOTELS_AVAILABILITY_PATH}/${destinationCode}?${searchParams.toString()}`;
    } else {
      return null;
    }
  }, [
    dateRange.from,
    dateRange.until,
    guests,
    isFormValid,
    place,
    roomsCount,
    sessionInfo?.hopperUserId,
    sessionInfo?.userDetails.tenantUserId,
    sessionInfo?.userDetails.communityId,
    staysPlatformEnabled,
  ]);

  const handleSearch = useCallback(() => {
    if (availabilityPath != null) {
      try {
        const url = new URL(availabilityPath, window.location.href);
        // If we go to /(locale?)/stays/, we're using the Commerce Portal so we can't just use navigate.
        if (url.pathname.match(/^(\/([a-zA-Z0-9-_]+))?\/stays\/availability/)) {
          window.location.href = availabilityPath;
          return;
        }
      } catch (error) {
        console.error("Error parsing availability path:", error);
      }
      navigate(availabilityPath);
    }
  }, [availabilityPath, navigate]);

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSearch();
  };

  return {
    context: {
      dateRange,
      guests,
      place,
      roomsCount,
      locationLabel: t("core-hotels.search.location.label"),
      searchButtonLabel: t("core-hotels.search.button"),
      isFormValid,
      availabilityPath,
    },
    handlers: {
      handleDateRangeFromChange,
      handleDateRangeUntilChange,
      handlePlaceChange,
      handleGuestsChange,
      handleRoomsCountChange,
      onFormSubmit,
      handleSearch,
    },
  };
};
